import { Injectable, inject } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';

import { IApplicants, IPropertyAndLoan, IUiAffordabilityModel, errorMessages } from '@clubhub/ignite/models/affordability';
import { IgniteService } from '@clubhub/ignite/services/ignite.service';
import { FormFieldType, WrapperType, yesNoOptions } from '@shared/components/formly/formly.config';
import { FormsValidators } from '@shared/components/forms/validators/forms.validators';
import {
  ApplicantCountry,
  BtlType,
  HolidayLetFrequency,
  MortgageLenders,
  MortgageType,
  ProductLength,
  ProductType,
  PropertyType,
  RemortgageReason,
  RemortgageType,
  RepaymentMethod,
  SkipListKeys,
  TaxStatusBtl,
  UkLocation,
} from '@shared/models';
import { AuthorizeService, FormatService, LtvService } from '@shared/services';
import { IgniteHelperService } from '@shared/services/ignite.helper';
import { LenderService } from '@shared/services/lender.service';
import { map } from 'rxjs';
import { PropertySearchService } from '../../property/property-search.service';

@Injectable({
  providedIn: 'root',
})
export class AffordabilityFormFieldsBtlService {
  private readonly helperService = inject(IgniteHelperService);
  private readonly igniteService = inject(IgniteService);
  private readonly authService = inject(AuthorizeService);
  private readonly formatService = inject(FormatService);
  private readonly lenderService = inject(LenderService);
  private readonly ltvService = inject(LtvService);
  private readonly propertySearchService = inject(PropertySearchService);

  public get model(): IUiAffordabilityModel | undefined {
    return this.igniteService.model;
  }

  public get allowCriteriaPlus() {
    return this.authService.allowCriteriaPlus;
  }

  public fields: FormlyFieldConfig[] = [
    // Step 1
    {
      key: 'propertyAndLoan',
      wrappers: [WrapperType.FormCard],
      props: {
        title: 'Property & loan',
      },
      validators: {
        max500PercentageLoanValue: {
          expression: (_control: AbstractControl, field: FormlyFieldConfig) =>
            this.ltvService.isLtvValid(field.form?.getRawValue().propertyAndLoan as IPropertyAndLoan),
          message: errorMessages.ltv500Percent,
          errorPath: 'loanAmount',
        },
        lessThanDeposit: {
          expression: (control: AbstractControl, field: FormlyFieldConfig) => {
            const { loanAmount, propertyValue } = field.form?.getRawValue().propertyAndLoan as IPropertyAndLoan;
            const equityLoanValue = control.value.equityLoanValue;
            if (!equityLoanValue) {
              return true;
            }

            return equityLoanValue < propertyValue - loanAmount;
          },
          message: 'Equity loan value must be less than deposit',
          errorPath: 'equityLoanValue',
        },
        interestOnlyAmount: {
          expression: (control: AbstractControl, field: FormlyFieldConfig) => {
            const { loanAmount, interestOnlyAmount } = field.form?.getRawValue().propertyAndLoan as IPropertyAndLoan;
            if (!interestOnlyAmount) {
              return true;
            }

            return +interestOnlyAmount < +loanAmount;
          },
          message: errorMessages.interestOnlyLessThanLoanAmount,
          errorPath: 'interestOnlyAmount',
        },
        productTransferInitialDate: this.helperService.getProductTransferInitialPeriodValidation(),
      },
      fieldGroup: [
        {
          type: FormFieldType.Template,
          template: '<span class="text-colour-red-600 fw-bold">*</span> mandatory fields',
        },
        {
          key: 'limitedCompanyPurchase',
          type: FormFieldType.RadioButtons,
          defaultValue: false,
          props: {
            label: 'Limited company purchase',
            options: yesNoOptions,
            required: true,
          },
        },
        {
          key: 'limitedCompanySpecialPurposeVehicle',
          type: FormFieldType.RadioButtons,
          defaultValue: false,
          props: {
            label: 'Is the limited company in a special purpose vehicle?',
            options: yesNoOptions,
            required: true,
          },
          expressions: {
            hide: field => !field.model?.limitedCompanyPurchase,
          },
        },
        {
          key: 'portfolioLandlord',
          type: FormFieldType.RadioButtons,
          defaultValue: false,
          props: {
            label: 'Portfolio landlord',
            helpText: 'Minimum of 4 properties (including this application)',
            options: yesNoOptions,
            required: true,
          },
        },
        {
          key: 'numberOfProperties',
          type: FormFieldType.Number,
          defaultValue: false,
          props: {
            label: 'How many properties in the portfolio?',
            min: 4,
            required: true,
          },
          expressions: {
            hide: field => !field.model?.portfolioLandlord,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
        },
        {
          key: 'location',
          type: FormFieldType.Select,
          props: {
            label: 'Property location',
            change: (field: FormlyFieldConfig) => {
              const { location } = field.model;
              if (location === UkLocation.NorthernIreland) {
                field.form?.patchValue([{ helpToBuy: false }]);
              }
              this.igniteService.skipListChange.next([SkipListKeys.PropertyLocation, SkipListKeys.HelpToBuyScheme]);
            },
            required: true,
            options: [
              { value: UkLocation.EastMidlands, label: 'East Midlands' },
              { value: UkLocation.EastOfEngland, label: 'East of England' },
              { value: UkLocation.GreaterLondon, label: 'Greater London' },
              { value: UkLocation.NorthEast, label: 'North East' },
              { value: UkLocation.NorthWest, label: 'North West' },
              { value: UkLocation.SouthEast, label: 'South East' },
              { value: UkLocation.SouthWest, label: 'South West' },
              { value: UkLocation.WestMidlands, label: 'West Midlands' },
              { value: UkLocation.YorkshireAndHumberside, label: 'Yorkshire & Humberside' },
              { value: UkLocation.NorthernIreland, label: 'Northern Ireland' },
              { value: UkLocation.Scotland, label: 'Scotland' },
              { value: UkLocation.Wales, label: 'Wales' },
            ],
          },
        },
        {
          key: 'propertyType',
          type: FormFieldType.RadioButtons,
          defaultValue: PropertyType.House,
          props: {
            label: 'Property type',
            options: [
              { label: 'House', value: PropertyType.House },
              { label: 'Flat', value: PropertyType.Flat },
            ],
            required: true,
          },
        },
        {
          key: 'btlType',
          type: FormFieldType.Select,
          defaultValue: BtlType.Standard,
          props: {
            label: 'BTL type',
            options: this.helperService.productBtlTypeOptions,
            required: true,
          },
        },
        {
          key: 'numberOfBedrooms',
          type: FormFieldType.Number,
          defaultValue: false,
          props: {
            label: 'Number of bedrooms',
            min: 2,
            max: 20,
            required: true,
          },
          expressions: {
            hide: field => field.model?.btlType !== BtlType.HMO,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
        },
        {
          key: 'numberOfBlocks',
          type: FormFieldType.Number,
          defaultValue: false,
          props: {
            label: 'Number of units',
            min: 2,
            required: true,
          },
          expressions: {
            hide: field => field.parent?.model?.btlType !== BtlType.MUFB,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
        },
        {
          key: 'propertySelfContainedFlats',
          type: FormFieldType.RadioButtons,
          defaultValue: false,
          props: {
            label: 'Does the property have self contained flats?',
            options: yesNoOptions,
            required: true,
          },
          expressions: {
            hide: field => field.model?.btlType !== BtlType.MUFB,
          },
        },
        {
          key: 'newBuild',
          type: FormFieldType.RadioButtons,
          defaultValue: false,
          props: {
            label: 'New build',
            options: yesNoOptions,
            required: true,
          },
        },
        {
          key: 'mortgageType',
          type: FormFieldType.RadioButtons,
          props: {
            label: 'Mortgage type',
            options: [
              { label: 'Purchase', value: MortgageType.Purchase },
              { label: 'Remortgage', value: MortgageType.Remortgage },
            ],
            required: true,
          },
        },
        {
          key: 'firstTimeBuyer',
          type: FormFieldType.RadioButtons,
          props: {
            label: 'First time buyer',
            options: yesNoOptions,
            required: true,
          },
          expressions: {
            hide: field => field.model?.mortgageType !== MortgageType.Purchase,
          },
        },
        {
          key: 'firstTimeLandlord',
          type: FormFieldType.RadioButtons,
          props: {
            label: 'First time landlord',
            options: yesNoOptions,
            required: true,
          },
          expressions: {
            hide: field => field.model?.mortgageType !== MortgageType.Purchase,
          },
        },
        {
          key: 'ownerOccupier',
          type: FormFieldType.RadioButtons,
          props: {
            label: 'Owner occupier',
            options: yesNoOptions,
            required: true,
          },
          expressions: {
            hide: field =>
              field.model?.limitedCompanyPurchase ||
              field.model?.firstTimeBuyer ||
              field.model?.firstTimeBuyer === null ||
              field.model?.mortgageType !== MortgageType.Purchase,
          },
        },
        {
          key: 'remortgageType',
          type: FormFieldType.Select,
          props: {
            label: 'Remortgage type',
            options: [
              { label: 'Let to buy', value: RemortgageType.LetToBuy },
              { label: 'Like for like', value: RemortgageType.LikeForLike },
              { label: 'Capital raising', value: RemortgageType.CapitalRaising },
            ],
            required: true,
          },
          expressions: {
            hide: field => (field.model as IPropertyAndLoan)?.mortgageType !== MortgageType.Remortgage,
          },
        },
        ...this.mortgageLenderFields(),
        {
          key: 'amountTransferredFromOtherLender',
          type: FormFieldType.Currency,
          defaultValue: 0,
          props: {
            label: 'Current mortgage balance outstanding',
            helpText: 'What is the outstanding mortgage balance from the previous lender?',
            required: true,
          },
          expressions: {
            hide: field =>
              field.model?.mortgageType !== MortgageType.Remortgage || field.model?.remortgageType !== RemortgageType.CapitalRaising,
          },
        },
        {
          key: 'propertyValue',
          type: FormFieldType.Currency,
          props: {
            label: 'Property value',
            required: true,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
          expressions: {
            'props.readonly': () => this.propertySearchService.hasPresetPropertyValue,
          },
        },
        {
          key: 'loanAmount',
          type: FormFieldType.Currency,
          props: {
            label: 'Loan amount',
            required: true,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
          expressions: {
            'props.readonly': () => this.propertySearchService.hasPresetPropertyValue,
          },
        },
        {
          key: 'ltv',
          wrappers: [WrapperType.HtmlWrapper],
          expressions: {
            'props.html': field => {
              const ltv = this.ltvService.calculateLtv(field.model as IPropertyAndLoan);

              if (!ltv) {
                return '';
              }
              return `
                <div class="ltv-display">
                  LTV ${ltv}%
                </div>
              `;
            },
          },
        },
        {
          key: 'equityLoanValue',
          type: FormFieldType.Currency,
          defaultValue: 0,
          props: {
            label: 'Equity loan value',
            helpText: `<p>
              Enter the amount of the equity loan from the government scheme.
              DO NOT include any of the customer's own deposit.
            </p>`,
          },
          expressions: {
            hide: field => field.model?.location === UkLocation.NorthernIreland || !field.model?.helpToBuy,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
        },
        {
          key: 'equityLoanValue',
          type: FormFieldType.Currency,
          defaultValue: 0,
          props: {
            label: 'Equity loan value',
            helpText:
              'Enter the amount of the equity loan from the government scheme. DO NOT include any of the customer\'s own deposit.',
            required: true,
          },
          expressions: {
            hide: field => field.model?.location === UkLocation.NorthernIreland || !field.model?.helpToBuy,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
        },
        {
          key: 'remortgageReason',
          type: FormFieldType.Select,
          props: {
            label: 'Reason for remortgage',
            helpText: 'What is the intention for the Help to Buy Equity loan Remortgage?',
            options: [
              { label: 'Purchase additional shares', value: RemortgageReason.PurchaseAdditionalShares },
              { label: '£ for £ remortgage', value: RemortgageReason.PoundForPound },
              { label: 'Other', value: RemortgageReason.Other },
            ],
            required: true,
          },
          expressions: {
            hide: field =>
              (field.model as IPropertyAndLoan).mortgageType !== MortgageType.Remortgage || !(field.model as IPropertyAndLoan).helpToBuy,
            'props.disabled': field => (field.model as IPropertyAndLoan).productTypeExtended === ProductType.RetirementInterestOnly,
          },
        },
        ...this.repaymentMethodFields(true),
        {
          key: 'holidayLetIncome',
          expressions: {
            hide: field => field.parent?.model?.btlType !== BtlType.HolidayLet,
          },
          props: {
            suppressErrors: true,
          },
          fieldGroup: [
            {
              template: `
                <h2 class="fw-light mt-4 mb-3">Holiday let income</h2>
              `,
            },
            {
              key: 'holidayLetIncomeLow',
              type: FormFieldType.Currency,
              defaultValue: 0,
              props: {
                label: 'Low season rental amount received / expected',
              },
            },
            {
              key: 'holidayLetIncomeMedium',
              type: FormFieldType.Currency,
              defaultValue: 0,
              props: {
                label: 'Mid season rental amount received / expected',
              },
            },
            {
              key: 'holidayLetIncomeHigh',
              type: FormFieldType.Currency,
              props: {
                label: 'High season rental amount received / expected',
              },
            },
            {
              key: 'holidayLetIncomeFrequency',
              type: FormFieldType.Select,
              defaultValue: null,
              props: {
                options: this.helperService.buildSelectOptionsFromEnum(HolidayLetFrequency),
                label: 'Payment frequency',
                selectClass: 'mb-5',
              },
              expressions: {
                'props.required': field =>
                  !!(field.model?.holidayLetIncomeLow || field.model?.holidayLetIncomeMedium || field.model?.holidayLetIncomeHigh),
              },
            },
          ],
        },
        {
          key: 'rentalAmountReceivedExpected',
          type: FormFieldType.Currency,
          props: {
            label: 'Monthly rental amount',
            helpText: 'Expected or received',
          },
          expressions: {
            'props.required': field => field.model?.btlType !== BtlType.HolidayLet,
            hide: field =>
              field.model?.btlType === BtlType.HolidayLet &&
              !!(field.model?.holidayLetIncome?.holidayLetIncomeLow ||
                field.model?.holidayLetIncome?.holidayLetIncomeMedium ||
                field.model?.holidayLetIncome?.holidayLetIncomeHigh),
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
        },
        {
          key: 'interestOnlyAmount',
          type: FormFieldType.Currency,
          props: {
            label: 'Amount on interest only',
            required: true,
          },
          expressions: {
            hide: field => field.model?.repaymentMethod.value !== RepaymentMethod.InterestOnlyPartAndPart,
          },
          validators: {
            validation: [FormsValidators.greaterThan(0)],
          },
        },
        {
          wrappers: [WrapperType.HtmlWrapper],
          expressions: {
            hide: field => (field.model as IPropertyAndLoan).repaymentMethod.value !== RepaymentMethod.InterestOnlyPartAndPart,
            'props.html': field => {
              const { loanAmount, interestOnlyAmount } = field.model as IPropertyAndLoan;
              const repaymentAmount = this.helperService.calculateRepaymentAmount(loanAmount, interestOnlyAmount);

              return `
                <div class="ltv-display">
                  Repayment amount £${repaymentAmount ? this.formatService.formatCurrency(repaymentAmount) : 0}
                </div>
              `;
            },
          },
        },
        {
          key: 'mortgageTerm',
          props: {
            label: 'Mortgage term',
            required: true,
            groupedField: true,
          },
          validators: {
            ...this.helperService.yearMonthValidator,
            max40Years: {
              expression: (control: AbstractControl) => {
                const { years, months } = control.value;
                return this.helperService.isMortgageTermValid(years, months);
              },
              message: this.max40YearsValidationMessage,
            },
          },
          fieldGroupClassName: 'row',
          fieldGroup: this.helperService.yearsAndMonthsFields(),
        },
        {
          key: 'productLength',
          type: FormFieldType.Select,
          defaultValue: ProductLength.TwoYears,
          props: {
            label: 'Initial product period',
            options: this.helperService.productLengthBtlOptions,
            required: true,
          },
        },
      ],
    },
    // Step 2
    {
      key: 'applicants',
      wrappers: [WrapperType.FormCard],
      props: {
        title: 'Applicants',
      },
      validators: {
        totalDependants: {
          expression: (control: AbstractControl) => {
            const { financialDependants, dependantsAged0To5, dependantsAged6To11, dependantsAged12To17, dependantsAged18AndOver } =
              control.value as IApplicants;
            if (!financialDependants) {
              return true;
            }
            const total = +dependantsAged0To5 + +dependantsAged6To11 + +dependantsAged12To17 + +dependantsAged18AndOver;
            return total > 0 && total <= 20;
          },
          message: 'Total dependants must be between 1 and 20',
          errorPath: 'dependantsAged18AndOver',
        },
      },
      fieldGroup: [
        {
          key: 'numberOfApplicants',
          type: FormFieldType.RadioButtons,
          defaultValue: 1,
          props: {
            label: 'Number of applicants',
            options: [
              { label: '1', value: 1 },
              { label: '2', value: 2 },
              { label: '3', value: 3 },
              { label: '4', value: 4 },
            ],
            required: true,
            change: (field: FormlyFieldConfig) => {
              const numberOfApplicants = field.model.numberOfApplicants;
              this.onNumberOfApplicantsChange(numberOfApplicants, numberOfApplicants);

              this.fields = [...this.fields];
            },
          },
        },
        {
          key: 'applicantAges',
          type: FormFieldType.Repeating,
          props: {
            suppressErrors: true,
          },
          wrappers: [WrapperType.NoWrappers],
          fieldArray: {
            wrappers: [WrapperType.NoWrappers],
            props: {
              suppressErrors: true,
            },
            fieldGroup: [
              {
                expressions: {
                  template: field => {
                    return `
                      <h2 class="fw-light mt-4 mb-3">Applicant ${field.model.applicantNumber}</h2>
                    `;
                  },
                },
              },
              {
                key: 'dateOfBirth',
                type: FormFieldType.Date,
                props: {
                  label: 'Date of birth',
                  required: true,
                },
                validators: {
                  validation: [FormsValidators.date, FormsValidators.atLeast18Years, FormsValidators.dateNotBefore126YearsAgo],
                },
              },
              {
                key: 'countryResident',
                type: FormFieldType.Select,
                props: {
                  label: 'Current country of residence',
                  options: this.helperService.countryResidentTypeOptions,
                  required: true,
                },
              },
              {
                key: 'grossAnnualIncome',
                type: FormFieldType.Currency,
                props: {
                  label: 'Gross annual income',
                  required: true,
                  helpText: 'Include total of all income before tax',
                },
                validators: {
                  validation: [FormsValidators.greaterThan(0)],
                },
              },
              {
                key: 'taxStatusBtl',
                type: FormFieldType.Select,
                props: {
                  label: 'Tax status',
                  required: true,
                },
                validators: {
                  validation: [FormsValidators.isEnumMember(TaxStatusBtl)],
                },
                expressions: {
                  'props.options': field => {
                    const countryResident = field.model?.countryResident;
                    if (countryResident === ApplicantCountry.Scotland) {
                      return this.helperService
                        .buildSelectOptionsFromEnum(TaxStatusBtl)
                        .filter(option => option.value !== TaxStatusBtl.Additional);
                    }
                    return this.helperService
                      .buildSelectOptionsFromEnum(TaxStatusBtl)
                      .filter(
                        option =>
                          option.value !== TaxStatusBtl.Top &&
                          option.value !== TaxStatusBtl.Starter &&
                          option.value !== TaxStatusBtl.Intermediate,
                      );
                  },
                  hide: field => field.model?.countryResident === null,
                },
              },
            ],
          },
        },
      ],
    },
    {
      key: 'proceedBtn',
      type: 'buttons',
      props: {
        numColumns: 4,
        buttons: [
          {
            label: 'Search',
            type: 'submit',
            className: 'btn-colour-blue-600',
            testId: 'affordability-search-noCriteria-form-button',
          },
          {
            label: 'Add criteria',
            type: 'button',
            className: 'btn-outline-colour-grayscale-900',
            eventName: 'submitWithCriteria', // emit a submitWithCriteria event which we can listen for on formly-form element's container
            testId: 'affordability-search-withCriteria-form-button',
          },
        ],
      },
    },
  ];

  private max40YearsValidationMessage(error: any, field: FormlyFieldConfig): string {
    return `${field.parent?.props?.text} has a maximum of 40 years`;
  }

  private mortgageLenderFields(): FormlyFieldConfig[] {
    const mortgageLenderHidden = (field: any) => {
      return (
        (field?.model?.mortgageType !== MortgageType.Remortgage && field?.model?.mortgageType !== MortgageType.Purchase) ||
        field?.model?.firstTimeBuyer === true
      );
    };

    const initialPeriodHidden = (field: any) => field?.model?.mortgageType !== MortgageType.Remortgage;

    return [
      {
        key: 'mortgageLender',
        wrappers: [WrapperType.NoWrappers],
        props: {
          suppressErrors: true,
        },
        fieldGroup: [
          {
            key: 'value',
            type: FormFieldType.Select,
            props: {
              label: 'Who is the current mortgage lender?',
              allowNull: true,
            },
            expressions: {
              hide: field => mortgageLenderHidden(field.parent?.parent),
              'props.options': this.lenderService
                .getUnarchivedLendersNames(this.helperService.getBtlLendingType())
                .pipe(map(lenderNames => this.helperService.getMortgageLendersList(lenderNames))),
            },
          },
        ],
      },
      {
        key: 'currentMortgageLender',
        type: FormFieldType.Input,
        expressions: {
          hide: field => {
            if (mortgageLenderHidden(field)) {
              return true;
            }

            const mortgageLenderValue = field.model?.mortgageLender?.value;
            return !mortgageLenderValue || mortgageLenderValue !== MortgageLenders.Other;
          },
        },
      },
      {
        key: 'productTransferInitialDate',
        type: FormFieldType.Date,
        props: {
          label: 'What is the end date for the initial period?',
        },
        validators: {
          validation: [FormsValidators.initialPeriodDate],
        },
        expressions: {
          hide: field => {
            if (initialPeriodHidden(field)) {
              return true;
            }

            return field.model?.mortgageLender?.value === MortgageLenders.Other;
          },
        },
      },
    ];
  }

  private repaymentMethodFields(showAllFields: boolean): FormlyFieldConfig[] {
    return [
      {
        key: 'repaymentMethod',
        props: {
          required: true,
          suppressErrors: true,
        },
        expressions: {
          'props.label': field => {
            if (showAllFields) {
              return 'Repayment method';
            }
            switch (field.model as RepaymentMethod) {
              case RepaymentMethod.InterestOnly:
                return 'Repayment method - interest only';
              case RepaymentMethod.InterestOnlyPartAndPart:
                return 'Repayment method - interest only part & part';
            }
          },
        },
        fieldGroup: [
          {
            key: 'value',
            type: FormFieldType.Select,
            hide: !showAllFields,
            props: {
              label: 'Repayment method',
              hideLabel: true,
              options: this.helperService.buildSelectOptionsFromEnum(RepaymentMethod),
              required: true,
              nested: true,
              collapseMargin: true,
            },
            expressions: {
              'props.readonly': field =>
                field.parent?.parent?.model.helpToBuy ||
                (field.parent?.parent?.formControl as UntypedFormGroup).controls?.productTypeExtended?.value ===
                  ProductType.RetirementInterestOnly,
            },
          },
        ],
      },
    ];
  }

  private onNumberOfApplicantsChange(numberOfApplicants: number, numberOfIncomesNeeded: number) {
    if (!this.model) {
      this.igniteService.updateModel();
    }
    if (this.model?.applicants?.applicantAges) {
      if ((this.model.applicants.applicantAges?.length ?? 0) > numberOfApplicants) {
        this.model.applicants.applicantAges = this.model.applicants.applicantAges.slice(0, numberOfApplicants);
      }
      if (
        (this.model.incomeAndExpenditure?.applicants?.length ?? 0) > numberOfIncomesNeeded &&
        this.model.incomeAndExpenditure?.applicants
      ) {
        this.model.incomeAndExpenditure.applicants = this.model.incomeAndExpenditure.applicants.slice(0, numberOfIncomesNeeded);
      }

      if (this.model.applicants.applicantAges) {
        while (this.model.applicants.applicantAges.length < numberOfApplicants) {
          this.igniteService.addApplicant();
        }
      }
      while ((this.model.incomeAndExpenditure?.applicants?.length ?? 0) < numberOfIncomesNeeded) {
        this.igniteService.addApplicantIncome();
      }
    }

    this.igniteService.updateModel();
  }
}
